import { Observable } from "rxjs/Observable"
import { Map } from "immutable"

import {
    AUTOCOMPLETE_REQUEST,
    AUTOCOMPLETE_COMMUNITY_LABELS_REQUEST,
    AUTOCOMPLETE_COMMUNITY_SYSTEM_LABELS_REQUEST,
    AUTOCOMPLETE_USERS_WITH_EMAIL_REQUEST,
    AUTOCOMPLETE_CONCEPTS_REQUEST,
    AUTOCOMPLETE_PROJECTS_REQUEST,
    AUTOCOMPLETE_SOURCES_REQUEST,
    AUTOCOMPLETE_RESPONSE
} from "../actions/action-types"
import {
    autocompleteResponse,
    autocompleteErrorResponse
} from "../actions/autocomplete-actions"
import { mapRequestError } from "./default-epics/epic-utils"
import { callbackActionCreatorEpic } from "./default-epics/default-entity-epics"
import { unauthenticated } from "../actions/authentication-actions"
import { autocomplete } from "../api/autocomplete-api"
import {
    getCommunityPossibleLabels,
    getCommunityPossibleSystemLabels
} from "../api/community-api"
import { getSourceList } from "../api/source-api"
import { getProjectList } from "../api/project-api"
import { getConceptList } from "../api/concept-api"
import { getUserList } from "../api/user-api"

export const requestAutocompleteEpic = createAutocompleteEpic(
    AUTOCOMPLETE_REQUEST,
    autocomplete,
    null,
    data =>
        data.map(item => ({
            ...item,
            displayName: item.name + (` (${item.label})` || "")
        }))
)

export const requestCommunityLabelsAutocompleteEpic = createAutocompleteEpic(
    AUTOCOMPLETE_COMMUNITY_LABELS_REQUEST,
    getCommunityPossibleLabels,
    "community",
    taggifyData
)

export const requestCommunitySystemLabelsAutocompleteEpic = createAutocompleteEpic(
    AUTOCOMPLETE_COMMUNITY_SYSTEM_LABELS_REQUEST,
    getCommunityPossibleSystemLabels,
    "community",
    taggifyData
)

export const requestConceptAutocompleteEpic = createAutocompleteEpic(
    AUTOCOMPLETE_CONCEPTS_REQUEST,
    getConceptList,
    null
)

export const requestProjectAutocompleteEpic = createAutocompleteEpic(
    AUTOCOMPLETE_PROJECTS_REQUEST,
    getProjectList,
    null
)

export const requestSourceAutocompleteEpic = createAutocompleteEpic(
    AUTOCOMPLETE_SOURCES_REQUEST,
    getSourceList,
    null
)

export const requestUsersWithEmailAutocompleteEpic = createAutocompleteEpic(
    AUTOCOMPLETE_USERS_WITH_EMAIL_REQUEST,
    getUserList,
    Map({ has_primary_email: true })
)

export const responseActionCreatorEpic = callbackActionCreatorEpic(
    AUTOCOMPLETE_RESPONSE
)

function createAutocompleteEpic(
    type,
    autocompleteAPI = autocomplete,
    staticParameters = null,
    dataProcessor = data => data,
    responseCreator = autocompleteResponse,
    errorResponseCreator = autocompleteErrorResponse
) {
    return (action$, { getState }) =>
        action$
            .ofType(type)
            .debounceTime(500)
            .mergeMap(
                ({
                    searchTerm,
                    entityType,
                    callback,
                    callbackActionCreator
                }) => {
                    const selectedEntityType = entityType || staticParameters
                    if (!searchTerm || !searchTerm.length) {
                        if (callback) {
                            callback(null, {
                                searchTerm,
                                options: dataProcessor([])
                            })
                        }
                        return Observable.of(
                            responseCreator(
                                [],
                                selectedEntityType,
                                searchTerm,
                                callbackActionCreator
                            )
                        )
                    }
                    return callAutocompleteAPI(
                        autocompleteAPI,
                        getState().authentication.token,
                        selectedEntityType,
                        searchTerm
                    )
                        .map(data => {
                            if (callback) {
                                callback(null, {
                                    searchTerm,
                                    options: dataProcessor(data)
                                })
                            }
                            return responseCreator(
                                data,
                                selectedEntityType,
                                searchTerm,
                                callbackActionCreator
                            )
                        })
                        .catch(error => {
                            if (callback) {
                                callback(error)
                            }
                            return Observable.of(
                                mapRequestError(
                                    error,
                                    unauthenticated,
                                    errorResponseCreator,
                                    selectedEntityType,
                                    searchTerm
                                )
                            )
                        })
                }
            )
}

function callAutocompleteAPI(autocompleteAPI, token, parameters, searchTerm) {
    if (parameters && Array.isArray(parameters))
        return autocompleteAPI(token, searchTerm, ...parameters)
    else return autocompleteAPI(token, searchTerm, parameters)
}

function taggifyData(data) {
    if (data && Array.isArray(data)) {
        return data.map(item => ({
            id: item,
            name: item
        }))
    }
    return data
}
